import React, { useState, useEffect, useRef, useCallback } from 'react';
import ModuleLayout from '../../../layouts/ModuleLayout';
import { useAuth } from '../../../context/AuthContext';
import whatsappService from '../../../services/whatsappService';
import {
  MessageSquare, Smartphone, QrCode, CheckCircle2, XCircle, Loader2,
  RefreshCw, LogOut, Send, Phone, Clock, ArrowLeft, AlertTriangle,
  Wifi, WifiOff, ShieldCheck, Activity, ChevronDown, ChevronUp,
} from 'lucide-react';

// ── Status Config ────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  connected:    { icon: CheckCircle2, label: 'Connected',     badgeClass: 'bg-emerald-100 text-emerald-700', dotClass: 'bg-emerald-500' },
  qr_ready:     { icon: QrCode,       label: 'Scan QR Code',  badgeClass: 'bg-amber-100 text-amber-700',     dotClass: 'bg-amber-500' },
  connecting:   { icon: Loader2,      label: 'Connecting...',  badgeClass: 'bg-blue-100 text-blue-700',       dotClass: 'bg-blue-500' },
  disconnected: { icon: WifiOff,      label: 'Disconnected',   badgeClass: 'bg-slate-100 text-slate-500',     dotClass: 'bg-slate-400' },
  error:        { icon: XCircle,      label: 'Error',          badgeClass: 'bg-red-100 text-red-600',         dotClass: 'bg-red-500' },
};

function formatPhone(phone) {
  if (!phone) return '—';
  const p = phone.replace(/\D/g, '');
  if (p.length >= 12) return `+${p.slice(0, 2)} ${p.slice(2, 7)} ${p.slice(7)}`;
  return `+${p}`;
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// ── QR Code Section ──────────────────────────────────────────────────────
function QRSection({ qrData, status, onRefresh }) {
  if (status === 'connected') return null;

  return (
    <div className="flex flex-col items-center py-6">
      {qrData ? (
        <>
          <div className="p-4 bg-white rounded-2xl shadow-lg border border-slate-200">
            <img src={qrData} alt="WhatsApp QR Code" className="w-64 h-64 rounded-lg" />
          </div>
          <div className="mt-4 text-center max-w-xs">
            <p className="text-sm font-medium text-slate-700">Open WhatsApp on your phone</p>
            <p className="text-xs text-slate-500 mt-1">
              Go to <span className="font-medium">Settings → Linked Devices → Link a Device</span> and scan this QR code
            </p>
          </div>
          <button onClick={onRefresh} className="mt-3 flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">
            <RefreshCw size={12} /> Refresh QR
          </button>
        </>
      ) : status === 'connecting' ? (
        <div className="flex flex-col items-center py-8">
          <Loader2 size={36} className="text-teal-500 animate-spin" />
          <p className="mt-3 text-sm text-slate-500">Connecting to WhatsApp...</p>
        </div>
      ) : (
        <div className="flex flex-col items-center py-8">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center">
            <QrCode size={32} className="text-slate-400" />
          </div>
          <p className="mt-3 text-sm text-slate-500">QR code loading...</p>
          <button onClick={onRefresh} className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs text-teal-600 hover:bg-teal-50 rounded-lg transition-colors">
            <RefreshCw size={12} /> Retry
          </button>
        </div>
      )}
    </div>
  );
}

// ── Connected Device Card ────────────────────────────────────────────────
function ConnectedDevice({ info, onLogout, isLoggingOut }) {
  return (
    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-5">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
            <Smartphone size={24} className="text-emerald-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-900">Linked Device</h3>
              <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Active
              </span>
            </div>
            <p className="text-sm text-slate-600 mt-0.5">{formatPhone(info.phone)}</p>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="bg-white/70 rounded-lg px-3 py-2">
          <p className="text-[11px] text-slate-500 uppercase tracking-wide">Connected Since</p>
          <p className="text-sm font-medium text-slate-700 mt-0.5">
            {info.connectedAt ? new Date(info.connectedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
          </p>
        </div>
        <div className="bg-white/70 rounded-lg px-3 py-2">
          <p className="text-[11px] text-slate-500 uppercase tracking-wide">Reconnects</p>
          <p className="text-sm font-medium text-slate-700 mt-0.5">{info.reconnectAttempts ?? 0}</p>
        </div>
      </div>

      <div className="mt-4">
        <button onClick={onLogout} disabled={isLoggingOut}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50">
          {isLoggingOut ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
          {isLoggingOut ? 'Disconnecting...' : 'Disconnect'}
        </button>
      </div>
    </div>
  );
}

// ── Test Message Section ─────────────────────────────────────────────────
function TestMessage({ branchId, isConnected }) {
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('Hello! This is a test e-bill from MaverickDine. 🧾');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const handleSend = async () => {
    if (!phone.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const fullPhone = phone.trim().startsWith('91') ? phone.trim() : '91' + phone.trim();
      const res = await whatsappService.send(branchId, fullPhone, message);
      setResult({ type: 'success', text: `Message sent to ${formatPhone(res.phone)}` });
      setPhone('');
    } catch (err) {
      setResult({ type: 'error', text: err.response?.data?.error || err.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        <Send size={16} className="text-teal-600" />
        <h3 className="font-semibold text-slate-900 text-sm">Test Message</h3>
      </div>
      <div className="p-5 space-y-3">
        <div>
          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Phone Number</label>
          <div className="mt-1 flex items-center">
            <div className="flex items-center gap-1.5 px-2.5 py-2 rounded-l border border-r-0 border-slate-200 bg-slate-50 text-sm font-semibold text-slate-600">
              <Phone size={13} />
              <span>+91</span>
            </div>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
              placeholder="10-digit mobile number" maxLength={10} disabled={!isConnected}
              className="flex-1 px-2.5 py-2 border rounded-r border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Message</label>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} disabled={!isConnected}
            className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none" />
        </div>
        <button onClick={handleSend} disabled={!isConnected || sending || !phone.trim() || phone.trim().length < 10}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          {sending ? 'Sending...' : 'Send Test Message'}
        </button>
        {result && (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${result.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
            {result.type === 'success' ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
            {result.text}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Message Logs ─────────────────────────────────────────────────────────
function MessageLogs({ branchId, isConnected }) {
  const [logs, setLogs] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const loadLogs = useCallback(async () => {
    try { setLogs(await whatsappService.getLogs(branchId, 20)); } catch { setLogs(null); }
  }, [branchId]);

  useEffect(() => { if (expanded && isConnected) loadLogs(); }, [expanded, isConnected, loadLogs]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-teal-600" />
          <h3 className="font-semibold text-slate-900 text-sm">Message Log</h3>
          {logs?.stats && <span className="text-xs text-slate-400">{logs.stats.totalSent} sent · {logs.stats.totalFailed} failed</span>}
        </div>
        {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>
      {expanded && (
        <div className="border-t border-slate-100">
          {logs?.stats && (
            <div className="px-5 py-3 bg-slate-50 grid grid-cols-4 gap-4">
              {[
                { label: 'Session Sent', value: logs.stats.sent, color: 'emerald' },
                { label: 'Session Failed', value: logs.stats.failed, color: 'red' },
                { label: 'Total Sent', value: logs.stats.totalSent, color: 'teal' },
                { label: 'Total Failed', value: logs.stats.totalFailed, color: 'slate' },
              ].map(({ label, value, color }) => (
                <div key={label} className="text-center">
                  <p className={`text-lg font-bold text-${color}-600`}>{value}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</p>
                </div>
              ))}
            </div>
          )}
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            {logs?.messages?.length > 0 ? logs.messages.map((msg) => (
              <div key={msg.id} className="px-5 py-2.5 flex items-center gap-3 text-sm hover:bg-slate-50">
                {msg.status === 'sent' ? <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" /> : <XCircle size={14} className="text-red-400 flex-shrink-0" />}
                <span className="text-slate-700 font-mono text-xs">{formatPhone(msg.phone)}</span>
                <span className="text-slate-400 text-xs ml-auto">{timeAgo(msg.timestamp)}</span>
              </div>
            )) : (
              <div className="px-5 py-6 text-center text-sm text-slate-400">No messages yet</div>
            )}
          </div>
          <div className="px-5 py-2 border-t border-slate-100">
            <button onClick={loadLogs} className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700">
              <RefreshCw size={11} /> Refresh
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════
export default function WhatsAppSettingsPage() {
  const { user } = useAuth();
  const branchId = user?.branchId;

  const [status, setStatus] = useState('disconnected');
  const [info, setInfo] = useState({});
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serviceError, setServiceError] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const pollRef = useRef(null);

  // ── Poll for status ───────────────────────────────────────────────────
  // Fetch status once (no continuous re-renders)
  const fetchStatus = useCallback(async () => {
    if (!branchId) {
      setServiceError('Branch ID not found. Please re-login.');
      setLoading(false);
      return;
    }

    try {
      const data = await whatsappService.getStatus(branchId);
      setServiceError(null);

      // Only update state if something actually changed
      setInfo(prev => {
        if (prev.status === data.status && prev.phone === data.phone) return prev;
        return data;
      });
      setStatus(prev => {
        if (prev === data.status) return prev;
        return data.status;
      });

      // Fetch QR only once when status becomes qr_ready
      if (data.status === 'qr_ready' && !qrData) {
        try {
          const qr = await whatsappService.getQR(branchId);
          if (qr.qr) setQrData(qr.qr);
        } catch { /* QR not ready yet */ }
      } else if (data.status !== 'qr_ready') {
        setQrData(null);
      }
    } catch (err) {
      setServiceError(
        err.message?.includes('Network Error')
          ? 'WhatsApp service is not running. Start it with: cd whatsapp-service && npm run dev'
          : `Service error: ${err.message}`
      );
      setStatus('error');
    } finally {
      setLoading(false);
    }
  }, [branchId, qrData]);

  // Initial fetch + poll only when NOT connected
  useEffect(() => {
    fetchStatus();

    // Poll every 4s only while waiting for QR scan / connection
    // Stop polling once connected
    const poll = () => {
      pollRef.current = setTimeout(async () => {
        if (!branchId) return;
        try {
          const data = await whatsappService.getStatus(branchId);
          setServiceError(null);

          if (data.status !== status) {
            setStatus(data.status);
            setInfo(data);
          }

          if (data.linked) {
            // Connected — update final state, stop polling
            setStatus('connected');
            setInfo(data);
            setQrData(null);
            return; // don't schedule next poll
          }

          if (data.status === 'qr_ready' && !qrData) {
            try {
              const qr = await whatsappService.getQR(branchId);
              if (qr.qr) setQrData(qr.qr);
            } catch {}
          }
        } catch {}

        poll(); // schedule next poll only if not connected
      }, 4000);
    };

    // Only start polling if not already connected
    if (status !== 'connected') {
      poll();
    }

    return () => clearTimeout(pollRef.current);
  }, []);

  // ── Actions ───────────────────────────────────────────────────────────
  const handleLogout = async () => {
    if (!window.confirm('Disconnect this WhatsApp number? You will need to scan the QR code again to reconnect.')) return;
    setIsLoggingOut(true);
    try {
      await whatsappService.logout(branchId);
      setStatus('disconnected');
      setInfo({});
      setQrData(null);
      setTimeout(fetchStatus, 2000);
    } catch (err) {
      alert('Failed to disconnect: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleRefreshQR = async () => {
    try {
      await whatsappService.reconnect(branchId);
      setTimeout(fetchStatus, 2000);
    } catch { fetchStatus(); }
  };

  const isConnected = status === 'connected';
  const sc = STATUS_CONFIG[status] || STATUS_CONFIG.disconnected;
  const StatusIcon = sc.icon;

  return (
    <ModuleLayout>
      <div className="flex flex-col h-full bg-slate-50">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => window.history.back()} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
                <ArrowLeft size={18} />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <MessageSquare size={18} className="text-emerald-600" />
                </div>
                <div>
                  <h1 className="text-base font-semibold text-slate-900">WhatsApp Integration</h1>
                  <p className="text-xs text-slate-500">Send e-bills directly to customers</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sc.badgeClass}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${sc.dotClass} ${isConnected ? 'animate-pulse' : ''}`} />
                {sc.label}
              </div>
              <button onClick={fetchStatus} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors" title="Refresh">
                <RefreshCw size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto p-6 space-y-5">

            {/* Service Error */}
            {serviceError && (
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                <AlertTriangle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800">Service Unavailable</p>
                  <p className="text-xs text-red-600 mt-1 font-mono">{serviceError}</p>
                </div>
              </div>
            )}

            {/* Loading */}
            {loading ? (
              <div className="flex flex-col items-center py-16">
                <Loader2 size={32} className="text-teal-500 animate-spin" />
                <p className="mt-3 text-sm text-slate-500">Checking WhatsApp connection...</p>
              </div>
            ) : (
              <>
                {/* Main Connection Card */}
                <div className={`rounded-xl border overflow-hidden ${isConnected ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="p-5">
                    {isConnected ? (
                      <ConnectedDevice info={info} onLogout={handleLogout} isLoggingOut={isLoggingOut} />
                    ) : (
                      <>
                        <div className="text-center mb-2">
                          <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center mx-auto mb-3">
                            <StatusIcon size={28} className={status === 'qr_ready' ? 'text-amber-600' : 'text-slate-400'} />
                          </div>
                          <h2 className="text-lg font-semibold text-slate-900">
                            {status === 'qr_ready' ? 'Scan QR Code to Link' : 'Link Your WhatsApp'}
                          </h2>
                          <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
                            Link your restaurant's WhatsApp number to send e-bills directly to customers without opening WhatsApp.
                          </p>
                        </div>
                        <QRSection qrData={qrData} status={status} onRefresh={handleRefreshQR} />
                      </>
                    )}
                  </div>
                </div>

                {/* How it works — not connected */}
                {!isConnected && (
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <h3 className="font-semibold text-slate-900 text-sm mb-4">How it works</h3>
                    <div className="space-y-3">
                      {[
                        { step: '1', title: 'Scan QR code above', desc: 'Use any personal WhatsApp number — no business account needed' },
                        { step: '2', title: 'Session stays active', desc: 'Survives server restarts. Re-scan only if you logout or after 14 days of inactivity' },
                        { step: '3', title: 'Bills sent automatically', desc: 'When staff sends an e-bill, customer receives it on WhatsApp from your number' },
                      ].map(({ step, title, desc }) => (
                        <div key={step} className="flex gap-3">
                          <div className="w-7 h-7 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-teal-700">{step}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-700">{title}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Test Message — connected */}
                {isConnected && <TestMessage branchId={branchId} isConnected={isConnected} />}

                {/* Message Logs — connected */}
                {isConnected && <MessageLogs branchId={branchId} isConnected={isConnected} />}

                {/* Info Notice */}
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <ShieldCheck size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Important Notes</p>
                    <ul className="mt-1.5 text-xs text-amber-700 space-y-1 list-disc list-inside">
                      <li>Keep daily messages under 50-100 to avoid WhatsApp restrictions</li>
                      <li>Messages are sent from your linked number — customers see your restaurant's number</li>
                      <li>Session auto-expires after 14 days of no activity</li>
                      <li>Each branch has its own WhatsApp session — link separately per branch</li>
                    </ul>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </ModuleLayout>
  );
}
